import React, { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Input, Label, Textarea } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast, Toaster } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { Plus, Trash2, X, Building2, Globe, Save } from "lucide-react";

const SIZES = ["small", "medium", "large", "enterprise"];

export default function OrganizationsPage() {
  const { user: me } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const empty = { name: "", industry: "", country: "", size: "medium", website: "", description: "" };
  const [form, setForm] = useState(empty);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/organizations");
      setItems(data.items);
    } catch (e) {
      console.error("Failed to load organizations", e);
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.patch(`/organizations/${editing.id}`, form);
        toast.success("Organization updated");
      } else {
        await api.post("/organizations", form);
        toast.success("Organization created");
      }
      setShowForm(false);
      setEditing(null);
      setForm(empty);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    }
  };

  const del = async (o) => {
    if (!window.confirm(`Delete organization "${o.name}"?`)) return;
    try {
      await api.delete(`/organizations/${o.id}`);
      toast.success("Deleted");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    }
  };

  return (
    <div className="space-y-6">
      <Toaster richColors position="top-right" />
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Organizations</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Manage the organizations governed by this CCAMS instance.
          </p>
        </div>
        {me?.role === "admin" && (
          <Button data-testid="org-new" onClick={() => { setEditing(null); setForm(empty); setShowForm(true); }}>
            <Plus className="w-4 h-4" /> New organization
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {loading && Array.from({ length: 3 }).map((_, i) => (
          <div key={`org-skel-${i}`} className="card-surface h-40 animate-pulse" />
        ))}
        {!loading && items.length === 0 && (
          <div className="col-span-full text-center text-muted-foreground py-8">
            No organizations yet.
          </div>
        )}
        {items.map((o) => (
          <Card key={o.id} data-testid={`org-card-${o.id}`}>
            <CardContent className="p-6">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-md bg-primary-subtle text-primary dark:bg-primary/15 flex items-center justify-center shrink-0">
                  <Building2 className="w-5 h-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-semibold truncate">{o.name}</div>
                  <div className="text-xs text-muted-foreground mt-0.5 truncate">
                    {o.industry || "—"} · {o.country || "—"}
                  </div>
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    {o.size && <Badge variant="primary">{o.size}</Badge>}
                    {o.website && (
                      <a
                        href={o.website}
                        target="_blank"
                        rel="noreferrer"
                        className="text-xs inline-flex items-center gap-1 text-muted-foreground hover:text-primary"
                      >
                        <Globe className="w-3 h-3" /> {new URL(o.website).hostname}
                      </a>
                    )}
                  </div>
                  {o.description && (
                    <p className="text-xs text-muted-foreground mt-3 line-clamp-2">
                      {o.description}
                    </p>
                  )}
                </div>
              </div>
              {me?.role === "admin" && (
                <div className="mt-4 flex justify-end gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    data-testid={`org-edit-${o.id}`}
                    onClick={() => {
                      setEditing(o);
                      setForm({
                        name: o.name || "",
                        industry: o.industry || "",
                        country: o.country || "",
                        size: o.size || "medium",
                        website: o.website || "",
                        description: o.description || "",
                      });
                      setShowForm(true);
                    }}
                  >
                    Edit
                  </Button>
                  <Button size="sm" variant="destructive" data-testid={`org-delete-${o.id}`} onClick={() => del(o)}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
          <div className="w-full max-w-2xl card-surface animate-fade-in" onClick={(e) => e.stopPropagation()} data-testid="org-form-modal">
            <div className="flex items-center justify-between p-4 border-b border-border">
              <h3 className="text-base font-semibold">{editing ? "Edit organization" : "New organization"}</h3>
              <button onClick={() => setShowForm(false)} className="p-1 rounded hover:bg-muted"><X className="w-4 h-4" /></button>
            </div>
            <form onSubmit={submit} className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <Label>Name</Label>
                <Input data-testid="org-form-name" className="mt-1.5" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
              </div>
              <div>
                <Label>Industry</Label>
                <Input className="mt-1.5" value={form.industry} onChange={(e) => setForm({ ...form, industry: e.target.value })} />
              </div>
              <div>
                <Label>Country</Label>
                <Input className="mt-1.5" value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} />
              </div>
              <div>
                <Label>Size</Label>
                <select
                  value={form.size}
                  onChange={(e) => setForm({ ...form, size: e.target.value })}
                  className="h-10 w-full rounded-md border border-border bg-card px-3 text-sm mt-1.5"
                >
                  {SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <Label>Website</Label>
                <Input className="mt-1.5" placeholder="https://example.com" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} />
              </div>
              <div className="md:col-span-2">
                <Label>Description</Label>
                <Textarea className="mt-1.5" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
              </div>
              <div className="md:col-span-2 flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
                <Button type="submit" data-testid="org-form-submit">
                  <Save className="w-4 h-4" />
                  {editing ? "Save changes" : "Create"}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
